import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { vehicleAPI } from '../../api/vehicles';
import { VehicleFuelEfficiency } from '../../types/entities';
import { Fuel, Search, AlertTriangle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { DateRangeQuickFilter } from '../../components/UI/DateRangeQuickFilter';

export const FuelEfficiency: React.FC = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState<VehicleFuelEfficiency[] | null>(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      toast.error(t('reports.selectDateRange'));
      return;
    }

    setLoading(true);
    try {
      const result = await vehicleAPI.getFuelEfficiencyReport({
        startDate,
        endDate,
      });
      setData(result);
    } catch (error) {
      toast.error(t('reports.generateError'));
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary
  const summary = data
    ? {
        totalVehicles: data.length,
        totalDistance: data.reduce((sum, v) => sum + v.totalDistanceKm, 0),
        totalFuelConsumed: data.reduce((sum, v) => sum + v.totalFuelConsumed, 0),
        totalExpectedFuel: data.reduce((sum, v) => sum + v.expectedFuel, 0),
        totalVariance: data.reduce((sum, v) => sum + v.totalVariance, 0),
        overallWastePercent:
          data.reduce((sum, v) => sum + v.expectedFuel, 0) > 0
            ? ((data.reduce((sum, v) => sum + v.totalFuelConsumed, 0) -
                data.reduce((sum, v) => sum + v.expectedFuel, 0)) /
                data.reduce((sum, v) => sum + v.expectedFuel, 0)) *
              100
            : 0,
      }
    : null;

  const getWasteColor = (wastePercent: number) => {
    if (wastePercent > 5) return 'text-red-600';
    if (wastePercent < 0) return 'text-green-600';
    return 'text-gray-700';
  };

  const getWasteBgColor = (wastePercent: number) => {
    if (wastePercent > 5) return 'bg-red-50';
    if (wastePercent < 0) return 'bg-green-50';
    return 'bg-gray-50';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Fuel className="w-7 h-7 text-primary-600" />
        <h1 className="text-2xl font-bold">{t('reports.fuelEfficiency')}</h1>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-end space-x-4">
          <DateRangeQuickFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="btn-primary flex items-center"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? t('common.loading') : t('reports.generate')}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('fuel.totalDistance')}</p>
            <p className="text-2xl font-bold text-blue-600">
              {summary.totalDistance.toLocaleString()} km
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('fuel.fuelConsumed')}</p>
            <p className="text-2xl font-bold text-orange-600">
              {summary.totalFuelConsumed.toFixed(1)} L
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('fuel.expectedFuel')}</p>
            <p className="text-2xl font-bold text-gray-600">
              {summary.totalExpectedFuel.toFixed(1)} L
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('fuel.fuelVariance')}</p>
            <p
              className={`text-2xl font-bold ${
                summary.totalVariance > 0 ? 'text-red-600' : 'text-green-600'
              }`}
            >
              {summary.totalVariance > 0 ? '+' : ''}
              {summary.totalVariance.toFixed(1)} L
            </p>
          </div>
          <div className={`card text-center ${getWasteBgColor(summary.overallWastePercent)}`}>
            <p className="text-sm text-gray-500">{t('fuel.wastePercent')}</p>
            <p className={`text-2xl font-bold ${getWasteColor(summary.overallWastePercent)}`}>
              {summary.overallWastePercent > 0 ? '+' : ''}
              {summary.overallWastePercent.toFixed(1)}%
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      {data && data.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3 table-header">{t('vehicles.licensePlate')}</th>
                <th className="pb-3 table-header">{t('vehicles.fuelType')}</th>
                <th className="pb-3 table-header text-right">
                  {t('vehicles.fuelNorm')} (L/100km)
                </th>
                <th className="pb-3 table-header text-right">{t('reports.tripCount')}</th>
                <th className="pb-3 table-header text-right">{t('fuel.totalDistance')}</th>
                <th className="pb-3 table-header text-right">{t('fuel.fuelConsumed')}</th>
                <th className="pb-3 table-header text-right">{t('fuel.expectedFuel')}</th>
                <th className="pb-3 table-header text-right">{t('fuel.wastePercent')}</th>
                <th className="pb-3 table-header text-center">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const isOverNorm = row.wastePercent > 5;
                const isUnderNorm = row.wastePercent < 0;

                return (
                  <tr
                    key={row.vehicleId}
                    className={`border-b last:border-0 ${isOverNorm ? 'bg-red-50' : ''}`}
                  >
                    <td className="py-3 font-medium">{row.licensePlate}</td>
                    <td className="py-3">
                      {row.fuelType === 'DIESEL'
                        ? t('vehicles.fuelTypes.DIESEL')
                        : t('vehicles.fuelTypes.GASOLINE')}
                    </td>
                    <td className="py-3 text-right">{row.fuelNorm}</td>
                    <td className="py-3 text-right">{row.tripCount}</td>
                    <td className="py-3 text-right">{row.totalDistanceKm.toLocaleString()} km</td>
                    <td className="py-3 text-right">{row.totalFuelConsumed.toFixed(1)} L</td>
                    <td className="py-3 text-right">{row.expectedFuel.toFixed(1)} L</td>
                    <td className={`py-3 text-right font-semibold ${getWasteColor(row.wastePercent)}`}>
                      {row.wastePercent > 0 ? '+' : ''}
                      {row.wastePercent.toFixed(1)}%
                    </td>
                    <td className="py-3 text-center">
                      {isOverNorm ? (
                        <span className="inline-flex items-center text-red-600">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          {t('fuel.overNorm')}
                        </span>
                      ) : isUnderNorm ? (
                        <span className="inline-flex items-center text-green-600">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {t('fuel.underNorm')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {t('fuel.withinNorm')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.length === 0 && (
        <div className="card text-center text-gray-500 py-10">{t('common.noData')}</div>
      )}
    </div>
  );
};
